import React from 'react';
import { Box, useTheme, keyframes } from '@mui/material';

interface ContentShimmerProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: number | string;
  variant?: 'text' | 'rectangular' | 'circular';
  lines?: number; // For text variant
  sx?: any;
}

// Shimmer animation keyframes
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const ContentShimmer: React.FC<ContentShimmerProps> = ({
  width = '100%',
  height = 16,
  borderRadius = 1,
  variant = 'rectangular',
  lines = 1,
  sx = {}
}) => {
  const theme = useTheme();

  const shimmerStyles = {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(90deg, #2A2A2A 0px, #3A3A3A 40px, #2A2A2A 80px)'
      : 'linear-gradient(90deg, #F0F0F0 0px, #E0E0E0 40px, #F0F0F0 80px)',
    backgroundSize: '200px 100%',
    animation: `${shimmer} 1.5s ease-in-out infinite`,
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'circular':
        return {
          borderRadius: '50%',
          width: height,
          height: height,
        };
      case 'text':
        return {
          borderRadius: 1,
          height: 16,
          width: width,
        };
      case 'rectangular':
      default:
        return {
          borderRadius,
          width,
          height,
        };
    }
  };

  // For text variant with multiple lines
  if (variant === 'text' && lines > 1) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
        {Array.from({ length: lines }).map((_, index) => (
          <Box
            key={index}
            sx={{
              ...shimmerStyles,
              ...getVariantStyles(),
              width: index === lines - 1 ? '70%' : width, // Last line is shorter
            }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...shimmerStyles,
        ...getVariantStyles(),
        ...sx,
      }}
    />
  );
};

export default ContentShimmer; 